<template>
  <div name="unitview" class="bg-gray-200">
    <div name="innertopbar" class="flex pt-4 pb-6 mx-6">
      <div
        class="text-black text-xl font-bold w-1/2 pt-4 pl-2 capitalize text-left"
      >
        {{ yearName }} -
        {{ course.name }}

        <div class="text-sm font-bold text-gray-600 p-1 pl-2 mt-1 w-5/12 rounded" v-bind:style="{ 'background-color': categoryColor }">{{ categoryName }} </div>
      </div>

      <div class="text-right w-2/3 pr-4">
        <ul class="mt-1 md:w-11/12 md:ml-6 inline-block">
          <li class="inline-block">
            <dropdown
              v-if="classes.length > 0"
              :options="classDropdown"
              :css="'text-white'"
              :selected="selectedClass"
              v-on:updateOption="classSelect"
              class="bg-action-blue bg-opacity-75 hover:bg-opacity-50 w-48 text-center"
            ></dropdown>
          </li>

          <li class="inline-block mx-4 text-2xl text-gray-400">|</li>

          <li class="inline-block" @click="closeWindow()">
            <i class="fas fa-times close-icon text-1xl" title="Close"></i>
          </li>
        </ul>
      </div>
    </div>

    <div class="h-10 flex content-center mx-8">
      <button
        type="button"
        class="header-subnav focus:outline-none"
        @click="showOverview()"
        v-bind:class="overviewInDisplay ? 'selected' : ''"
      >
        Content
      </button>
      <button
        type="button"
        class="header-subnav focus:outline-none"
        @click="showResources()"
        v-bind:class="showResourcesModal === true ? 'selected' : ''"
      >
        Resources
      </button>
      <button
        type="button"
        class="header-subnav focus:outline-none"
        @click="showEvidence()"
        v-bind:class="evidenceInDisplay ? 'selected' : ''"
      >
        Evidence
      </button>
      <button
        type="button"
        class="header-subnav focus:outline-none"
        @click="showAssessment()"
        v-bind:class="assessmentInDisplay ? 'selected' : ''"
      >
        Unit Assessment
      </button>
      <button
        type="button"
        class="header-subnav focus:outline-none"
        @click="showOutcomes()"
        v-bind:class="outcomesInDisplay ? 'selected' : ''"
      >
        Unit Outcomes
      </button>

      <button
        type="button"
        class="header-subnav focus:outline-none"
        @click="showWellbeing()"
        v-bind:class="wellbeingInDisplay ? 'selected' : ''"
      >
        Pupil Voice
      </button>
    </div>

    <div class="w-full mx-auto bg-white">
      <div
        class="h-16 flex overflow-x-auto content-center px-12 pl-14 border-b pt-2"
        ref="navList"
        v-if="lessonsInDisplay"
      >
        <button
          @click="selectLesson()"
          type="button"
          class="flex-none header-subnav px-2 mr-2 focus:outline-none"
          v-bind:class="!hasValidLessonSelected ? 'selected-lesson' : ''"
        >
          <i class="fas fa-caret-right text-gray-400 pr-2"></i> Overview
        </button>
        <button
          @click="selectLesson(lesson)"
          type="button"
          class="flex-none header-subnav px-2 mr-2 focus:outline-none"
          v-bind:class="isLessonSelected(lesson) ? 'selected-lesson' : ''"
          v-for="lesson in orderedLessons"
          :key="lesson.id"
        >
          <i class="fas fa-caret-right text-gray-400 pr-2"></i>
          {{ lesson.name }}
        </button>

        <div ref="endPoint" class="opacity-0">&nbsp;</div>

        <button
          v-if="isOverflowing()"
          @click="scrollIntoView"
          class="header-subnav absolute bg-white h-12 right-0 pr-2 focus:outline-none"
        >
          <i class="fas fa-chevron-right text-xl text-gray-500 pt-2"></i>
        </button>
      </div>

      <div class="flex flex-col md:flex-row">
        <div
          i
          class="w-full h-screen overflow-y-scroll mt-4 pb-4 mx-2"
          id="infinite-list"
        >
          <div class="mt-4 w-full">
            <div v-if="errored" class="empty">
              <p>
                We are not able to retrieve this information at the moment,
                please try back later
              </p>
            </div>

            <div id="content-area" v-else>
              <div style="width: 100%; text-align: center" v-if="loading">
                <i class="spinner fa fa-spinner fa-spin"></i>
              </div>

              <LessonContent
                v-if="overviewInDisplay && hasValidLessonSelected"
                :course="course"
                :pupils="filteredPupils"
                :lesson="selectedLesson"
                :key="'content' + selectedLesson.id"
              >
              </LessonContent>

              <courseOverview
                v-else-if="overviewInDisplay"
                :course="course"
                :lessons="lessons"
                @showLesson="selectLesson"
                @showAttendance="selectLessonWithAttendance"
                @showEvidence="selectLessonWithEvidence"
              >
              </courseOverview>

              <p v-if="!overviewInDisplay && lessonCount === 0">
                The selected unit has no lesson plans available. Please select a
                different unit of work.
              </p>
            </div>

            <Assessment
              :course="course"
              :key="'assessment' + course.id"
              :classSelected="classIsSelected"
              :pupils="filteredPupils"
              v-if="assessmentInDisplay"
            ></Assessment>

            <Outcomes
              :course="course"
              :key="'outcomes' + course.id"
              :pupils="filteredPupils"
              v-if="outcomesInDisplay"
            ></Outcomes>

            <Wellbeing
              :course="course"
              :key="'wellbeing' + course.id"
              :pupils="filteredPupils"
              v-if="wellbeingInDisplay"
            ></Wellbeing>

            <Evidence
              :key="'evidence' + course.id"
              :course="course"
              :pupils="filteredPupils"
              :selectedClass="selectedClass"
              :lesson="selectedLesson"
              :lessons="lessons"
              :classes="classes"
              v-if="evidenceInDisplay"
              ref="evidenceRef"
            ></Evidence>

            <ModalSlide
              :show="showResourcesModal"
              @closeModal="showResourcesModal = false"
            >
              <template v-slot:header-icon>
                <i class="fas fa-folder"></i>
              </template>
              <template v-slot:header-title> Unit Files & Resources </template>
              <template v-slot:body>
                <UnitFileList
                 :course="course"
                  :key="'files' + course.id"
                ></UnitFileList>
              </template>
            </ModalSlide>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const courseOverview = () =>
  import("@/components/curriculum/TheCourseOverview.vue");

const modalfile = () => import("@/components/ModalFile.vue");

const LessonContent = () => import("@/components/curriculum/TheLessonView.vue");

const Assessment = () =>
  import("@/components/curriculum/TheCourseAssessment.vue");
const Evidence = () => import("@/components/curriculum/TheLessonEvidence.vue");
const Outcomes = () => import("@/components/curriculum/TheCourseOutcomes.vue");
const Wellbeing = () => import("@/components/curriculum/TheCourseWellbeing.vue");
const dropdown = () => import("@/components/Dropdown.vue");
const UnitFileList = () => import("@/components/curriculum/TheUnitFileList");
const ModalSlide = () => import("@/components/ModalSlide.vue");

const DisplayOptions = {
  OVERVIEW: "overview",
  ASSESSMENT: "assessment",
  EVIDENCE: "evidence",
  OUTCOMES: "outcomes",
  WELLBEING: "wellbeing",
};

import { mapGetters } from "vuex";
import { SchoolService } from "@/services/school.service";
import { PupilService } from "@/services/pupil.service";

import { yearGroupMixin } from "@/mixins/yearGroupMixin.js";

export default {
  name: "unitview",
  mixins: [yearGroupMixin],
  computed: {
    ...mapGetters({
      currentUser: "currentUser",
      currentAccess: "currentAccess",
    }),
    lessonCount() {
      return this.lessons.length;
    },
    componentLoaded() {
      return this.lessons.length > 0;
    },
    orderedLessons() {
      return _.orderBy(this.lessons, ["order", "name"]);
    },
    overviewInDisplay() {
      return this.displaySelected === DisplayOptions.OVERVIEW;
    },
    evidenceInDisplay() {
      return this.displaySelected === DisplayOptions.EVIDENCE;
    },
    assessmentInDisplay() {
      return this.displaySelected === DisplayOptions.ASSESSMENT;
    },
    outcomesInDisplay() {
      return this.displaySelected === DisplayOptions.OUTCOMES;
    },
    wellbeingInDisplay() {
      return this.displaySelected === DisplayOptions.WELLBEING;
    },
    lessonsInDisplay() {
      return [DisplayOptions.OVERVIEW, DisplayOptions.EVIDENCE].includes(
        this.displaySelected
      );
    },
    hasValidLessonSelected() {
      return this.selectedLesson != undefined;
    },
    classDropdown() {
      var results = [...this.classes];
      results = [{ id: 0, name: "Select a class" }].concat(results);
      this.selectedClass = results[0];
      return results;
    },
    filteredPupils() {
      var vm = this;
      let classChosen = vm.selectedClass;

      if (classChosen.id === 0) {
        return _.orderBy(vm.pupils, ["name", "yeargroup", "class"]);
      } else {
        var filtered = vm.pupils.filter(function (pupil) {
          return pupil.inclass === classChosen.name;
        });

        return _.orderBy(filtered, ["name", "yeargroup", "class"]);
      }
    },
    classIsSelected() {
      return this.selectedClass.id > 0;
    },
    year() {
      return this.years
        .filter((year) => {
          return this.course.year_id === year.id;
        })
        .shift();
    },
    yearName() {
      let result = this.year
      return result.name
    },
    categoryName() {
      if (this.course === undefined) { return "" }
      return this.course.category.name
    },
    categoryColor() {
      if (this.course != undefined) {
        return "#" + this.course.category.color_hex
      }
    }
  },
  data() {
    return {
      course: this.$store.getters.unit,
      loading: true,
      errored: false,
      lessons: [],
      selectedLesson: undefined,
      showDocuments: false,
      displaySelected: DisplayOptions.OVERVIEW,
      pupils: [],
      classes: [],
      selectedClass: {},
      showResourcesModal: false,
    };
  },
  created() {
    this.loading = true;
    this.fetchData();

    PupilService.getClasses()
      .then((response) => this.buildClassList(response.data.data))
      .catch((err) => {
        console.log("classes " + err);
        this.errored = true;
      });


    this.fetchPupils();
  },
  mounted() {},
  methods: {
    fetchData() {
      SchoolService.getAll("courses/" + this.course.id + "/lessons")
        .then((response) => this.buildLessonList(response.data.data))
        .catch((err) => {
          console.log("Lessons " + err);
          this.errored = true;
        })
        .finally(() => (this.loading = false));
    },
    buildLessonList(data) {
      this.lessons = data;
    },
    fetchPupils() {
      PupilService.getAll()
        .then((response) => this.buildPupilList(response.data.data))
        .catch((err) => {
          console.log("Pupils " + err);
          this.errored = true;
        })
        .finally(() => (this.loading = false));
    },
    buildPupilList(data) {
      this.pupils = data;
    },
    buildClassList(data) {
      this.classes = data.map(function (item, index) {
        return { id: index + 1, name: item };
      });
    },
    closeWindow() {
      this.$router.push("/planning");
      window.scrollTo(0, 0);
    },
    print() {
      this.$htmlToPaper("printMe");
      //window.print();
      //   var newWindow = window.open();
      //   newWindow.document.write(document.getElementById("output").innerHTML);
      //   newWindow.print();
    },
    showOverview() {
      this.displaySelected = DisplayOptions.OVERVIEW;
    },
    showAssessment() {
      this.displaySelected = DisplayOptions.ASSESSMENT;
    },
    showOutcomes() {
      this.displaySelected = DisplayOptions.OUTCOMES;
    },
    showEvidence() {
      this.displaySelected = DisplayOptions.EVIDENCE;
    },
    showWellbeing() {
      this.displaySelected = DisplayOptions.WELLBEING;
    },
    showResources() {
      this.showResourcesModal = true
    },
    classSelect(value) {
      this.selectedClass = value;
    },
    selectLesson(value) {
      this.selectedLesson = value;
      this.$store.commit("setLesson", value);
    },
    selectLessonWithAttendance(value) {
      this.selectLesson(value);
    },
    selectLessonWithEvidence(value) {
      this.selectLesson(value);
      this.showEvidence();
    },
    isLessonSelected(lesson) {
      return (
        this.selectedLesson != undefined && this.selectedLesson.id === lesson.id
      );
    },
    isOverflowing() {
      var element = this.$refs.navList;

      if (element != undefined) {
        return element.offsetWidth < element.scrollWidth;
      }
    },
    scrollIntoView() {
      this.$refs["endPoint"].scrollIntoView({ behavior: "smooth" });
    },
  },
  components: {
    Assessment,
    Evidence,
    modalfile,
    Outcomes,
    dropdown,
    courseOverview,
    UnitFileList,
    LessonContent,
    ModalSlide,
    Wellbeing
  },
};
</script>

<style scoped>
.side-list-item {
  @apply p-4 px-2 mt-3 ml-4 mb-2 w-10/12 rounded-md font-medium cursor-pointer text-xs font-semibold;
}

.selected {
  @apply bg-white border-white;
}

.selected-lesson {
  @apply text-blue-500 font-bold border-b border-blue-500;
}

.selected-lesson i {
  @apply text-blue-500;
}
</style>
